import { getUniqueArray } from '@lib/utils'

import {
  Conversation,
  ConversationItem,
  MessageAttachment,
  Participant,
} from '../models'

type DependenciesResult = {
  listings: number[]
  profiles: number[]
  users: number[]
  contacts: number[]
}

const initialDependenciesResult: DependenciesResult = {
  listings: [],
  profiles: [],
  users: [],
  contacts: [],
}

const getAttachmentListingIds = (
  attachments?: MessageAttachment[]
): number[] => {
  if (!attachments || attachments.length === 0) {
    return []
  }

  return attachments
    .filter(
      (
        attachment
      ): attachment is Extract<MessageAttachment, { kind: 'Listing' }> =>
        attachment.kind === 'Listing'
    )
    .map((attachment) => attachment.listingId)
}

const extractParticipantValueByKey = (
  participants: Participant[],
  key: keyof Pick<Participant, 'userId' | 'profileId' | 'listingContactId'>
): Array<number> => {
  return participants.map((item) => item[key]!)
}

export const getDependenciesIDs = (
  conversations: Array<Conversation> | Record<number, ConversationItem>
): DependenciesResult => {
  const data = !Array.isArray(conversations)
    ? Object.values(conversations)
    : conversations

  const result = data.reduce<DependenciesResult>(
    (acc: DependenciesResult, item) => {
      const entityid =
        item.context?.listingId ||
        item.context?.projectId ||
        item.context?.unitId
      const listingsFromContext = entityid
        ? [...acc.listings, entityid]
        : acc.listings

      const attachmentsListingIds = (() => {
        const collected: number[] = []

        if ('messages' in item && Array.isArray(item.messages)) {
          item.messages.forEach((message) => {
            collected.push(...getAttachmentListingIds(message.attachments))
          })
        }

        if ('lastMessage' in item && item.lastMessage) {
          collected.push(
            ...getAttachmentListingIds(item.lastMessage.attachments)
          )
        }

        return collected
      })()

      const listings = [...listingsFromContext, ...attachmentsListingIds]

      const profiles = [
        ...acc.profiles,
        ...extractParticipantValueByKey(item.participants, 'profileId'),
      ]

      const users = [
        ...acc.users,
        ...extractParticipantValueByKey(item.participants, 'userId'),
      ]

      const contacts = [
        ...acc.contacts,
        ...extractParticipantValueByKey(item.participants, 'listingContactId'),
      ]

      return { listings, profiles, users, contacts }
    },
    initialDependenciesResult
  )

  return {
    listings: getUniqueArray<number>(result.listings),
    profiles: getUniqueArray<number>(result.profiles),
    users: getUniqueArray<number>(result.users),
    contacts: getUniqueArray<number>(result.contacts),
  }
}
